// «Сегодня у инженера» — мобильная стартовая по прототипу «Воздух» (дизайн-2026/vozduh,
// решение владельца 01.08.2026). Аудитория — 234 инженера, их главный экран на телефоне;
// на десктопе тот же блок стоит первой колонкой инженерного варианта Home.
//
// ЧТО ЗДЕСЬ ЕСТЬ И ОТКУДА. Четыре блока, каждый — из СУЩЕСТВУЮЩЕЙ ручки BFF, новых не
// заведено ни одной:
//   • «сегодня» и неделя — /api/sched/engineer (sched_api.py): свои полосы в окне от
//     сегодняшнего дня, ближайшие работы и отсутствия за краем окна;
//   • «требует действия» — /api/p73/verifications и /api/p73/job-reports с фильтром
//     engineer_bx (сотрудник Bitrix из привязки учётки) и status=draft,rejected:
//     черновик надо досдать, отклонённое — переделать. Оба статуса «на инженере»;
//   • «мои деньги» — /api/profile/me. Раздел живёт за флагом владельца: включённость
//     приезжает в /api/me (profile.enabled), выключенный блок не делает НИ ОДНОГО
//     запроса и не рисуется вовсе (та же механика, что в pages/MyProfile);
//   • быстрые переходы — плитки инженерного набора из profiles.ts, БЕЗ своих копий.
//
// ПЛИТКИ И БЛОКИ ФИЛЬТРУЮТСЯ ПРАВАМИ. Предикат тот же, что у меню, — denied() из
// useRightsApi: закрытый роли раздел не рисуется ни плиткой, ни блоком, и запрос за его
// данными не уходит. Это подсказка интерфейса, а не защита: дверь — в web/bff/rights.py.
//
// ЧИСЕЛ ИЗ ГОЛОВЫ НЕТ. Каждое число на экране — из ответа ручки; где ручки нет
// (рейтинг), там переход без числа. Ошибка одной ручки рисует плашку СВОЕГО блока
// (QueryState на каждом блоке, а не на экране целиком) и не роняет остальные —
// у инженера в море связь рвётся чаще, чем у нас.
//
// ЗАПИСИ ЗДЕСЬ НЕТ И НЕ БУДЕТ: сдача верификаций и отчётов — на портале 73, экран об
// этом говорит словами (принцип «честность про границы системы» из README «Воздуха»).
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { CoreUnsupported, Unauthorized } from '../../api/client'
import { useRightsApi } from '../../api/hooks'
import type { ВерификацияСтрока, ОтчётСтрока, СтраницаP73 } from '../../api/p73'
import { useСводкаКабинета, useФлагКабинета } from '../../api/profile'
import { useSelf } from '../../api/self'
import { QueryState } from '../../components/QueryState'
import { fmtDate, plural } from '../../lib/format'
import { ПУТИ, ТИПЫ_ВЕРИФИКАЦИИ, названиеСтатуса } from '../../lib/p73'
import { СВОДКИ } from './profiles'
import './engineer-today.css'

// ─────────────────────────── транспорт ───────────────────────────
// Свой GET, а не общий api/client (там эмуляция Bitrix REST) и не хуки api/p73 (у них
// нет `enabled`, а закрытый правами блок не должен слать НИ ОДНОГО запроса). Разбор
// ответа — тот же, что в api/p73.ts и api/profile.ts: 401 уводит на вход, 400 ядра
// поднимается отдельным типом.

async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответа /sched/engineer (см. sched_api.py, функция `инженер`) ───────────

interface ДеньОкна {
  date: string
  wd: string
  day: number
  month: number
  weekend: boolean
  today: boolean
}

/** Полоса в днях окна: смещение и длина. Общее у работ и отсутствий. */
interface Полоса {
  off: number
  days: number
  plan_start: string
  plan_end: string
}

interface МояРабота extends Полоса {
  deal_id: number
  title: string
  ship: string
  port: string
  stage_name: string
  wtype_name: string
}

interface МоёОтсутствие extends Полоса {
  kind: string
  kind_name: string
}

interface МойКалендарь {
  row: { engineer_id: number; name: string }
  window: { from: string; to: string; today: string; days: ДеньОкна[] }
  jobs: МояРабота[]
  absences: МоёОтсутствие[]
  /** Работы ЗА краем окна — чтобы пустой хвост не читался как «свободен и дальше». */
  next_jobs: Array<{ deal_id: number; title: string; ship: string; stage_name: string; start: string }>
  next_absences: Array<{ kind: string; kind_name: string; start: string; end: string }>
}

/**
 * Окно календаря: от сегодняшнего дня на две недели. Лентой показываются первые 7 дней,
 * остальное питает «ближайшую работу», когда сегодня пусто. back=0 — прошлое на этом
 * экране не нужно: вопрос дня — «где я сегодня и что от меня ждут».
 */
const ОКНО_ВПЕРЁД = 13
const ДНЕЙ_В_ЛЕНТЕ = 7

const накрывает = (п: Полоса, день: number) => п.off <= день && день < п.off + п.days

const ЧИСЛО = new Intl.NumberFormat('ru-RU')
const число = (v: number | null | undefined) =>
  (v === null || v === undefined ? '—' : ЧИСЛО.format(v))

const ОБЩЕЕ = { retry: 1, staleTime: 60_000 } as const

// ═══════════════════════════════ экран ═══════════════════════════════

export default function EngineerToday() {
  const права = useRightsApi()
  const self = useSelf()
  const [выбранДень, setВыбранДень] = useState(0)

  // Личные блоки живут на привязке учётки к сотруднику Bitrix. Нет привязки — нет
  // и запросов: спрашивать календарь «человека null» бессмысленно.
  const привязан = self.loaded && self.bitrixUserId !== null

  const календарьМожно = !права.denied('engineer-schedule')
  const календарь = useQuery({
    queryKey: ['etoday', 'sched', self.id],
    queryFn: () => запрос<МойКалендарь>(
      `/api/sched/engineer?id=${self.id}&back=0&fwd=${ОКНО_ВПЕРЁД}`),
    enabled: календарьМожно && привязан,
    ...ОБЩЕЕ,
  })

  const верифМожно = !права.denied('verifications')
  const вериф = useQuery({
    queryKey: ['etoday', 'verifications', self.id],
    queryFn: () => запрос<СтраницаP73<ВерификацияСтрока>>(
      `/api/p73/verifications?engineer_bx=${self.id}&status=draft,rejected`),
    enabled: верифМожно && привязан,
    ...ОБЩЕЕ,
  })

  const отчётыМожно = !права.denied('job-reports')
  const отчёты = useQuery({
    queryKey: ['etoday', 'job-reports', self.id],
    queryFn: () => запрос<СтраницаP73<ОтчётСтрока>>(
      `/api/p73/job-reports?engineer_bx=${self.id}&status=draft,rejected`),
    enabled: отчётыМожно && привязан,
    ...ОБЩЕЕ,
  })

  // «Мои деньги» — только когда раздел выдан учётке И владелец включил флаг кабинета.
  // Выключенный блок не спрашивает данные вовсе (см. api/profile.ts).
  const флаг = useФлагКабинета()
  const кабинетМожно = !права.denied('my-profile')
    && !!флаг.data?.enabled && !!флаг.data?.linked
  const кабинет = useСводкаКабинета(кабинетМожно)

  // ─────────── «сегодня»: одна главная карточка дня ───────────
  const к = календарь.data
  const дни = useMemo(() => (к?.window.days ?? []).slice(0, ДНЕЙ_В_ЛЕНТЕ), [к])
  const сегодняРабота = к?.jobs.find((р) => накрывает(р, 0))
  const сегодняОтсутствие = к?.absences.find((о) => накрывает(о, 0))
  const будущая = useMemo(
    () => (к?.jobs ?? []).filter((р) => р.off > 0).sort((a, b) => a.off - b.off)[0],
    [к],
  )
  const заОкном = к?.next_jobs?.[0]

  // ─────────── «требует действия»: черновики и отклонённое ───────────
  const дела = useMemo(() => {
    const из: Array<{ key: string; to: string; name: string; meta: string }> = []
    for (const v of вериф.data?.rows ?? []) {
      const тип = (ТИПЫ_ВЕРИФИКАЦИИ[v.type] ?? v.type ?? '').toLowerCase()
      из.push({
        key: `v${v.d1_id}`,
        to: ПУТИ.верификация(v.d1_id),
        name: `Верификация${тип ? ` — ${тип}` : ''}`,
        meta: [v.vessel?.name, названиеСтатуса(v.status), v.reject_reason]
          .filter(Boolean).join(' · '),
      })
    }
    for (const r of отчёты.data?.rows ?? []) {
      из.push({
        key: `r${r.d1_id}`,
        to: ПУТИ.отчёт(r.d1_id),
        name: 'Отчёт по заявке',
        meta: [r.deal?.title, r.vessel?.name, названиеСтатуса(r.status), r.reject_reason]
          .filter(Boolean).join(' · '),
      })
    }
    return из
  }, [вериф.data, отчёты.data])
  // Число — сумма total из ответов ядра, а не длина страницы: страниц может быть больше.
  const делВсего = (вериф.data?.total ?? 0) + (отчёты.data?.total ?? 0)

  // ─────────── быстрые переходы: плитки инженерного набора, без своих копий ───────────
  const переходы = СВОДКИ['инженер'].плитки.filter((п) => !права.denied(п.раздел))

  const деньги = кабинет.data

  return (
    <div className="etoday">
      {self.loaded && !привязан && (
        <div className="etoday-note">
          Учётка не связана с сотрудником Bitrix — личные блоки (календарь, «требует
          действия», деньги) показать нечем. Связать может администратор.
        </div>
      )}

      {/* ─────────── сегодня ─────────── */}
      {календарьМожно && привязан && (
        <section aria-label="Сегодня">
          {/* QueryState на КАЖДОМ блоке, а не на экране целиком: упавший календарь
              рисует свою плашку, а «требует действия» и переходы остаются живыми. */}
          <QueryState isLoading={календарь.isLoading} error={календарь.error}>
          {к && (
            <>
              <div className="etoday-card">
                {сегодняРабота ? (
                  <>
                    <div className="etoday-lab">Сегодня</div>
                    {сегодняРабота.ship && <div className="etoday-vessel">{сегодняРабота.ship}</div>}
                    <div className="etoday-work">{сегодняРабота.title}</div>
                    <div className="etoday-where">
                      {[сегодняРабота.port, сегодняРабота.wtype_name, сегодняРабота.stage_name]
                        .filter(Boolean).join(' · ')}
                    </div>
                    <div className="etoday-where">
                      {fmtDate(сегодняРабота.plan_start)} — {fmtDate(сегодняРабота.plan_end)}
                    </div>
                    <div className="etoday-row">
                      <Link className="etoday-btn primary" to={`/deals/${сегодняРабота.deal_id}`}>
                        Открыть сделку
                      </Link>
                      <Link className="etoday-btn" to="/engineer-schedule">Календарь</Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="etoday-lab">
                      {будущая || заОкном ? 'Ближайшая работа' : 'Сегодня'}
                    </div>
                    {будущая ? (
                      <>
                        {будущая.ship && <div className="etoday-vessel">{будущая.ship}</div>}
                        <div className="etoday-work">{будущая.title}</div>
                        <div className="etoday-where">
                          с {fmtDate(будущая.plan_start)}
                          {будущая.port ? ` · ${будущая.port}` : ''}
                        </div>
                        <div className="etoday-row">
                          <Link className="etoday-btn primary" to={`/deals/${будущая.deal_id}`}>
                            Открыть сделку
                          </Link>
                        </div>
                      </>
                    ) : заОкном ? (
                      <>
                        {заОкном.ship && <div className="etoday-vessel">{заОкном.ship}</div>}
                        <div className="etoday-work">{заОкном.title}</div>
                        <div className="etoday-where">с {fmtDate(заОкном.start)} · за краем двух недель</div>
                        <div className="etoday-row">
                          <Link className="etoday-btn primary" to={`/deals/${заОкном.deal_id}`}>
                            Открыть сделку
                          </Link>
                        </div>
                      </>
                    ) : (
                      <div className="etoday-work">
                        {сегодняОтсутствие
                          ? `${сегодняОтсутствие.kind_name}: ${fmtDate(сегодняОтсутствие.plan_start)}`
                            + ` — ${fmtDate(сегодняОтсутствие.plan_end)}`
                          : 'Работ на ближайшие две недели не назначено.'}
                      </div>
                    )}
                    {!будущая && !заОкном && к.next_absences?.[0] && !сегодняОтсутствие && (
                      <div className="etoday-where">
                        Дальше: {к.next_absences[0].kind_name.toLowerCase()} с{' '}
                        {fmtDate(к.next_absences[0].start)}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* ─────────── неделя: лента дней под палец ─────────── */}
              <h2 className="etoday-sec">Неделя</h2>
              <div className="etoday-week" role="group" aria-label="Дни недели">
                {дни.map((д, i) => {
                  const занят = к.jobs.some((р) => накрывает(р, i))
                  const отсут = к.absences.some((о) => накрывает(о, i))
                  return (
                    <button
                      key={д.date}
                      type="button"
                      className={`etoday-day${занят ? ' busy' : ''}${отсут ? ' away' : ''}`}
                      aria-pressed={i === выбранДень}
                      onClick={() => setВыбранДень(i)}
                    >
                      <span className="etoday-dw">{д.wd}</span>
                      <span className="etoday-dn num">{д.day}</span>
                      <span className="etoday-m" aria-hidden="true" />
                    </button>
                  )
                })}
              </div>
              <div className="etoday-day-detail" aria-live="polite">
                {(() => {
                  const д = дни[выбранДень]
                  if (!д) return null
                  const работы = к.jobs.filter((р) => накрывает(р, выбранДень))
                  const отсут = к.absences.filter((о) => накрывает(о, выбранДень))
                  if (!работы.length && !отсут.length) {
                    return <div className="etoday-dd-w">{д.weekend ? 'выходной' : 'работ нет'}</div>
                  }
                  return (
                    <>
                      {работы.map((р) => (
                        <div key={р.deal_id}>
                          <div className="etoday-dd-j">{р.ship || р.title}</div>
                          <div className="etoday-dd-w">
                            {[р.port, р.wtype_name].filter(Boolean).join(' · ') || р.title}
                          </div>
                        </div>
                      ))}
                      {отсут.map((о, i) => (
                        <div className="etoday-dd-w" key={`a${i}`}>{о.kind_name}</div>
                      ))}
                    </>
                  )
                })()}
              </div>
            </>
          )}
          </QueryState>
        </section>
      )}

      {/* ─────────── требует действия ─────────── */}
      {(верифМожно || отчётыМожно) && привязан && (
        <section aria-label="Требует действия">
          <h2 className="etoday-sec">
            Требует действия{делВсего > 0 ? ` · ${делВсего}` : ''}
          </h2>
          <QueryState isLoading={вериф.isLoading || отчёты.isLoading}
                      error={вериф.error ?? отчёты.error}>
            {дела.map((д) => (
              <Link className="etoday-item" to={д.to} key={д.key}>
                <span className="etoday-dot" aria-hidden="true" />
                <span className="etoday-t">
                  <span className="etoday-name">{д.name}</span>
                  {д.meta && <span className="etoday-meta">{д.meta}</span>}
                </span>
                <span className="etoday-chev" aria-hidden="true">›</span>
              </Link>
            ))}
            {дела.length === 0 && (вериф.data || отчёты.data) && (
              <div className="empty">
                <div className="empty-title">Всё сдано</div>
                <div className="empty-hint">Черновиков и отклонённого на вас нет.</div>
              </div>
            )}
          </QueryState>
          <div className="etoday-hint">Сдача — на портале 73, здесь только просмотр.</div>
        </section>
      )}

      {/* ─────────── мои деньги: только когда кабинет открыт учётке ─────────── */}
      {кабинетМожно && (
        <section aria-label="Мои деньги">
          <h2 className="etoday-sec">Мои деньги</h2>
          <QueryState isLoading={кабинет.isLoading} error={кабинет.error}>
          {деньги && (деньги.found ? (
            <div className="etoday-money">
              <div className="etoday-mcard">
                <div className="etoday-mv num">{число(деньги.money?.available_points)}</div>
                <div className="etoday-ml">баллы к выплате</div>
              </div>
              <div className="etoday-mcard">
                <div className="etoday-mv num">
                  {деньги.overtime?.work_hours === null || деньги.overtime?.work_hours === undefined
                    ? '—' : `${число(деньги.overtime.work_hours)} ч`}
                </div>
                <div className="etoday-ml">овертаймы</div>
              </div>
              <div className="etoday-mcard">
                <div className="etoday-mv num">{число(деньги.badges?.badges)}</div>
                <div className="etoday-ml">
                  {деньги.badges?.badges === null || деньги.badges?.badges === undefined
                    ? 'значки'
                    : plural(деньги.badges.badges, 'значок', 'значка', 'значков')}
                </div>
              </div>
            </div>
          ) : (
            <div className="etoday-note">{деньги.why || 'В зеркале портала 73 вас нет.'}</div>
          ))}
          </QueryState>
          <Link className="etoday-item" to="/my-profile">
            <span className="etoday-t">
              <span className="etoday-name">Личный кабинет</span>
              <span className="etoday-meta">начисления, выплаты, значки</span>
            </span>
            <span className="etoday-chev" aria-hidden="true">›</span>
          </Link>
        </section>
      )}

      {/* ─────────── быстрые переходы: плитки набора «инженер» ─────────── */}
      {переходы.length > 0 && (
        <section aria-label="Разделы">
          <h2 className="etoday-sec">Разделы</h2>
          {переходы.map((п) => (
            <Link className="etoday-item" to={п.to} key={п.to}>
              <span className="etoday-ic" aria-hidden="true">{п.ic}</span>
              <span className="etoday-t">
                <span className="etoday-name">{п.t}</span>
                <span className="etoday-meta">{п.d}</span>
              </span>
              <span className="etoday-chev" aria-hidden="true">›</span>
            </Link>
          ))}
        </section>
      )}
    </div>
  )
}
